import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaEdit, FaEye, FaPlus, FaSearch, FaTag, FaTrash } from "react-icons/fa";
import {
  apihubDelete,
  apihubGet,
  apihubPut,
} from "../../api/apihub";
import { postCreatetags } from "../../api/customer.api";
import { useProjectPhone } from "../../hooks/useProjectPhone";
import { hasPermission } from "../../utils/auth";

const emptyTagForm = () => ({ tagName: "", category: "" });

const formatDate = (value) => {
  if (!value) return "—";
  try {
    const d = new Date(value);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const time = d.toLocaleString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return `${y}-${m}-${day} ${time}`;
  } catch {
    return "—";
  }
};

const FetchAllTags = () => {
  const { projectId } = useProjectPhone();
  const canCreate = hasPermission("Tag_Create");
  const [tags, setTags] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyTagForm);
  const [createError, setCreateError] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [viewTag, setViewTag] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(emptyTagForm);
  const [saving, setSaving] = useState(false);
  const [maxTags, setMaxTags] = useState(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const [res, projectRes] = await Promise.all([
        apihubGet(
          `/getInteractedCustomerDetail/fetchtagsbyprojectid/${projectId}`,
        ),
        apihubGet(`/getInteractedCustomerDetail/projects/${projectId}`).catch(
          () => null,
        ),
      ]);
      const list = Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data)
          ? res.data
          : [];
      setTags(list);
      const project = projectRes?.project || projectRes?.data?.project || {};
      const limit = project?.noOfTags ?? project?.features?.noOfTags ?? null;
      setMaxTags(typeof limit === "number" ? limit : null);
    } catch (err) {
      setTags([]);
      setError(err.message || "Failed to fetch tags");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tags;
    return tags.filter(
      (t) =>
        (t.tagName || "").toLowerCase().includes(q) ||
        (t.category || "").toLowerCase().includes(q),
    );
  }, [search, tags]);

  const categories = useMemo(() => {
    const set = new Set();
    tags.forEach((t) => {
      if (t.category && t.category !== "-") set.add(t.category);
    });
    return [...set].sort();
  }, [tags]);

  const openCreate = () => {
    setCreateForm(emptyTagForm());
    setCreateError("");
    setError("");
    setMessage("");
    setViewTag(null);
    setEditOpen(false);
    setCreateOpen(true);
  };

  const closeCreate = () => {
    if (saving) return;
    setCreateOpen(false);
    setCreateError("");
  };

  const saveCreate = async (e) => {
    e.preventDefault();
    if (!projectId) return;
    const tagName = createForm.tagName.trim();
    if (!tagName) {
      setCreateError("Tag name is required.");
      return;
    }
    if (maxTags != null && tags.length >= maxTags) {
      setCreateError("Please upgrade your plan to add more tags.");
      return;
    }
    setSaving(true);
    setCreateError("");
    setError("");
    setMessage("");
    try {
      await postCreatetags({
        tagName,
        category: createForm.category.trim(),
        projectId,
      });
      setMessage("Tag created successfully.");
      setCreateForm(emptyTagForm());
      setCreateOpen(false);
      load();
    } catch (err) {
      setCreateError(err.message || "Failed to create tag");
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (tag) => {
    setEditId(tag._id);
    setEditForm({ tagName: tag.tagName || "", category: tag.category || "" });
    setEditOpen(true);
    setCreateOpen(false);
    setViewTag(null);
    setMessage("");
    setError("");
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editId) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await apihubPut(
        `/getInteractedCustomerDetail/findtagsandupdate/${editId}`,
        {
          tagName: editForm.tagName,
          category: editForm.category,
        },
      );
      setMessage("Tag updated successfully.");
      setEditOpen(false);
      load();
    } catch (err) {
      setError(err.message || "Failed to update tag");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (tagId) => {
    if (!projectId || !window.confirm("Are you sure you want to delete this tag?")) {
      return;
    }
    setError("");
    setMessage("");
    try {
      await apihubDelete(
        `/getInteractedCustomerDetail/deletetagsbytagid/${tagId}`,
        { tag_id: tagId, project_id: projectId },
      );
      setMessage("Tag deleted successfully.");
      if (viewTag?._id === tagId) setViewTag(null);
      load();
    } catch (err) {
      setError(err.message || "Failed to delete tag");
    }
  };

  return (
    <div className="tags-page">
      <header className="tags-header">
        <h1>
          <FaTag aria-hidden />
          Tags
        </h1>
        <p className="tags-subtitle">
          By using Tags/Labels, segment and organize conversations to segregate,
          retargeted follow ups - smarter and close faster.
        </p>
      </header>

      <section className="tags-guide-banner">
        <div className="tags-guide-text">
          <h2>Starter Guide</h2>
          <p>
            The first message indicates the user&apos;s interest in a specific
            product or service.
          </p>
        </div>
        <article className="tags-total-card">
          <div>
            <strong>{tags.length}</strong>
            <span>Total tag</span>
          </div>
          <span className="tags-total-deco" aria-hidden />
        </article>
      </section>

      <section className="tags-toolbar">
        <div className="tags-search">
          <FaSearch className="tags-search-icon" aria-hidden />
          <input
            type="search"
            placeholder="Search tags..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search tags"
          />
        </div>
        {canCreate && (
          <button type="button" className="tags-add-btn" onClick={openCreate}>
            <FaPlus />
            Add Tag
          </button>
        )}
      </section>

      {error && <p className="tags-alert tags-alert-error">{error}</p>}
      {message && <p className="tags-alert tags-alert-success">{message}</p>}

      <section className="tags-list-section">
        <h2 className="tags-list-title">Tags List</h2>
        <div className="tags-table-wrap">
          <table className="tags-table">
            <thead>
              <tr>
                <th>Tag Name</th>
                <th>Category</th>
                <th>CreatedAt</th>
                <th className="biz-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="tags-table-empty">
                    Loading tags…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={4} className="tags-table-empty">
                    No tags found.
                  </td>
                </tr>
              ) : (
                filtered.map((tag) => (
                  <tr key={tag._id}>
                    <td className="tags-name">{tag.tagName || "—"}</td>
                    <td>{tag.category || ""}</td>
                    <td>{formatDate(tag.createdAt)}</td>
                    <td className="biz-col-actions">
                      <div className="biz-table-actions tags-row-actions">
                        <button
                          type="button"
                          className="tags-action-btn tags-action-edit"
                          onClick={() => openEdit(tag)}
                          aria-label={`Edit ${tag.tagName}`}
                        >
                          <FaEdit />
                        </button>
                        <button
                          type="button"
                          className="tags-action-btn tags-action-delete"
                          onClick={() => remove(tag._id)}
                          aria-label={`Delete ${tag.tagName}`}
                        >
                          <FaTrash />
                        </button>
                        <button
                          type="button"
                          className="tags-action-btn tags-action-view"
                          onClick={() => setViewTag(tag)}
                          aria-label={`View ${tag.tagName}`}
                        >
                          <FaEye />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {createOpen && (
        <div className="biz-modal-backdrop" onClick={closeCreate}>
          <div
            className="biz-modal-card biz-attr-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="create-tag-title"
          >
            <button
              type="button"
              className="biz-modal-close"
              onClick={closeCreate}
              aria-label="Close"
            >
              ×
            </button>
            <h3 id="create-tag-title">Add Tag</h3>
            <form onSubmit={saveCreate}>
              <label className="biz-form-field">
                <span>
                  Tag Name <span className="biz-required">*</span>
                </span>
                <input
                  type="text"
                  value={createForm.tagName}
                  onChange={(e) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      tagName: e.target.value,
                    }))
                  }
                  placeholder="Enter tag name"
                  required
                  disabled={saving}
                />
              </label>
              <label className="biz-form-field">
                <span>Category</span>
                <select
                  value={createForm.category}
                  onChange={(e) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      category: e.target.value,
                    }))
                  }
                  disabled={saving}
                >
                  <option value="">Select a category</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </label>
              <label className="biz-form-field">
                <span>Or enter new category</span>
                <input
                  type="text"
                  value={createForm.category}
                  onChange={(e) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      category: e.target.value,
                    }))
                  }
                  placeholder="Enter category"
                  disabled={saving}
                />
              </label>
              {maxTags != null && (
                <p className="biz-field-hint">
                  Tags used: {tags.length} / {maxTags}
                </p>
              )}
              {createError && <p className="biz-form-error">{createError}</p>}
              <div className="biz-modal-actions">
                <button
                  type="button"
                  className="biz-btn-secondary"
                  onClick={closeCreate}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="biz-btn-primary"
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {viewTag && (
        <div className="tags-modal-backdrop" onClick={() => setViewTag(null)}>
          <div
            className="tags-modal-card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="view-tag-title"
          >
            <h3 id="view-tag-title">Tag details</h3>
            <dl className="tags-view-dl">
              <div>
                <dt>Tag Name</dt>
                <dd>{viewTag.tagName || "—"}</dd>
              </div>
              <div>
                <dt>Category</dt>
                <dd>{viewTag.category || "—"}</dd>
              </div>
              <div>
                <dt>Created At</dt>
                <dd>{formatDate(viewTag.createdAt)}</dd>
              </div>
              <div>
                <dt>Updated At</dt>
                <dd>{formatDate(viewTag.updatedAt)}</dd>
              </div>
            </dl>
            <div className="tags-modal-actions">
              <button
                type="button"
                className="tags-modal-btn-secondary"
                onClick={() => setViewTag(null)}
              >
                Close
              </button>
              <button
                type="button"
                className="tags-modal-btn-primary"
                onClick={() => openEdit(viewTag)}
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {editOpen && (
        <div className="tags-modal-backdrop" onClick={() => setEditOpen(false)}>
          <div
            className="tags-modal-card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="edit-tag-title"
          >
            <h3 id="edit-tag-title">Edit Tag</h3>
            <form onSubmit={saveEdit} className="tags-edit-form">
              <label className="tags-form-field">
                <span>Tag Name</span>
                <input
                  type="text"
                  value={editForm.tagName}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, tagName: e.target.value }))
                  }
                  required
                />
              </label>
              <label className="tags-form-field">
                <span>Category</span>
                <select
                  value={editForm.category}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, category: e.target.value }))
                  }
                >
                  <option value="">Select a category</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Or enter new category"
                  value={editForm.category}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, category: e.target.value }))
                  }
                  className="tags-category-custom"
                />
              </label>
              <div className="tags-modal-actions">
                <button
                  type="button"
                  className="tags-modal-btn-secondary"
                  onClick={() => setEditOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="tags-modal-btn-primary"
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FetchAllTags;
